import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import {
  EReportType,
  ERunReportQueryParam,
  ERunReportStep,
  ESearchType,
} from '@/pages/RunReport/types'
import { useListOfUseCasesQuery } from '@/store/api/RunReport/RunReport.query'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import {
  setCompanyInfo,
  setReportType,
  setSearchResultsInfo,
  setSubjectInfo,
  setUseCases,
} from '@/store/api/RunReport/RunReport.slice'
import { parseEnumQueryParam } from '@/utils/parseEnumQueryParam'

const DEFAULT_REPORT_TYPE = EReportType.AiAnalystReport
const DEFAULT_PLATFORM = 'linkedin'

export const SearchForm = () => {
  useRunReportGuard(ERunReportStep.SearchForm)

  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const slice = useAppSelector(selectRunReport)
  const { data: useCasesData, isLoading: isLoadingUseCases } =
    useListOfUseCasesQuery()

  const isCompanySearch =
    slice.selectSearchType.searchType === ESearchType.Company

  const reportTypeFromUrl = parseEnumQueryParam(
    searchParams.get(ERunReportQueryParam.ReportType),
    EReportType,
  )
  const reportType =
    reportTypeFromUrl ??
    (slice.selectSearchType.reportType as EReportType) ??
    DEFAULT_REPORT_TYPE

  useEffect(() => {
    if (slice.selectSearchType.reportType !== reportType) {
      dispatch(setReportType({ reportType }))
    }
  }, [reportType])

  const [firstName, setFirstName] = useState(slice.subjectInfo.firstName || '')
  const [middleName, setMiddleName] = useState(
    slice.subjectInfo.middleName || '',
  )
  const [lastName, setLastName] = useState(slice.subjectInfo.lastName || '')
  const [companyName, setCompanyName] = useState(slice.companyInfo.name || '')
  const [selectedUseCaseId, setSelectedUseCaseId] = useState(
    slice.useCases.selectedUseCaseId || '',
  )

  const visibleUseCases =
    useCasesData?.info.filter((useCase) => {
      if (isCompanySearch) return useCase.isForCompanySearch
      if (useCase.isForCompanySearch) return false
      if (reportType === EReportType.AiReport) {
        return !useCase.requiredComplianceForm
      }
      return true
    }) ?? []

  const selectedUseCase = useMemo(
    () => visibleUseCases.find((uc) => uc._id === selectedUseCaseId),
    [visibleUseCases, selectedUseCaseId],
  )

  const isValid = isCompanySearch
    ? companyName.trim().length > 0 && selectedUseCaseId.length > 0
    : firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      selectedUseCaseId.length > 0

  const getUseCaseTranslatedName = (
    useCase: NonNullable<typeof useCasesData>['info'][number],
  ) => {
    const translation = useCase.translations.find(
      (tr) => tr.language === i18n.language,
    )
    return translation?.name ?? useCase.name
  }

  const handleSearch = () => {
    if (!isValid || !selectedUseCase) return

    dispatch(
      setUseCases({
        selectedUseCaseId: selectedUseCase._id,
        selectedUseCaseName: selectedUseCase.name,
        consentSwitchValue: selectedUseCase.requiredComplianceForm,
        isSwitchDisabled: isCompanySearch,
      }),
    )

    if (isCompanySearch) {
      dispatch(
        setCompanyInfo({
          ...slice.companyInfo,
          name: companyName.trim(),
        }),
      )
      navigate(AppRoutes.runReportCompanyForm)
      return
    }

    const trimmedFirst = firstName.trim()
    const trimmedMiddle = middleName.trim()
    const trimmedLast = lastName.trim()
    const searchName = [trimmedFirst, trimmedLast].filter(Boolean).join(' ')

    dispatch(
      setSubjectInfo({
        ...slice.subjectInfo,
        firstName: trimmedFirst,
        middleName: trimmedMiddle,
        lastName: trimmedLast,
      }),
    )
    dispatch(
      setSearchResultsInfo({
        ...slice.searchResultsInfo,
        searchInput: searchName,
      }),
    )

    if (selectedUseCase.requiredComplianceForm) {
      navigate(AppRoutes.runReportConsentRequired)
      return
    }

    if (reportType === EReportType.AiReport) {
      const params = new URLSearchParams({
        [ERunReportQueryParam.Q]: searchName,
        [ERunReportQueryParam.UseCaseId]: selectedUseCase._id,
        [ERunReportQueryParam.Platform]: DEFAULT_PLATFORM,
        [ERunReportQueryParam.ReportType]: reportType,
      })
      navigate(`${AppRoutes.runReportSearchResults}?${params.toString()}`)
      return
    }

    navigate(AppRoutes.runReportSubjectForm)
  }

  const handleBack = () => {
    if (reportType === EReportType.AiReport) {
      navigate(AppRoutes.dashboard)
      return
    }
    navigate(AppRoutes.runReportStartSearch)
  }

  return (
    <WeScreen
      useNextButton={false}
      useSkipButton={false}
      onClickBack={handleBack}
      hideHeaderOnMobile
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-8 p-6">
        <div className="flex w-full flex-col items-center gap-3 text-center">
          <h2 className="text-[32px] font-medium leading-[normal] text-neutralColor">
            {t('dashboard.runReport.startSearch.title')}
          </h2>
          <p className="text-base text-neutralColor60">
            {t('dashboard.runReport.startSearch.subtitle')}
          </p>
        </div>

        <div className="flex w-full max-w-[580px] flex-col gap-[26px]">
          {isCompanySearch ? (
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={t(
                'dashboard.runReport.startSearch.companyNameLabel',
              )}
              aria-label={t('dashboard.runReport.startSearch.companyNameLabel')}
            />
          ) : (
            <>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t(
                  'dashboard.runReport.startSearch.firstNameLabel',
                )}
                aria-label={t('dashboard.runReport.startSearch.firstNameLabel')}
              />
              <Input
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
                placeholder={t(
                  'dashboard.runReport.startSearch.middleNameLabel',
                )}
                aria-label={t(
                  'dashboard.runReport.startSearch.middleNameLabel',
                )}
              />
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t('dashboard.runReport.startSearch.lastNameLabel')}
                aria-label={t('dashboard.runReport.startSearch.lastNameLabel')}
              />
            </>
          )}

          <Select
            value={selectedUseCaseId}
            onValueChange={setSelectedUseCaseId}
            disabled={isLoadingUseCases}
          >
            <SelectTrigger
              aria-label={t(
                'dashboard.runReport.startSearch.useCasePlaceholder',
              )}
            >
              <SelectValue
                placeholder={t(
                  'dashboard.runReport.startSearch.useCasePlaceholder',
                )}
              />
            </SelectTrigger>
            <SelectContent
              position="popper"
              side="bottom"
              sideOffset={4}
              avoidCollisions={false}
              className="w-[var(--radix-select-trigger-width)]"
            >
              <SelectGroup>
                {visibleUseCases.map((useCase) => (
                  <SelectItem
                    hiddenCheckIcon
                    key={useCase._id}
                    value={useCase._id}
                    endContent={
                      useCase.requiredComplianceForm ? (
                        <span className="text-dynamicBlue text-xs font-medium">
                          {t('dashboard.newSearch.useCases.consentRequired')}
                        </span>
                      ) : null
                    }
                  >
                    {getUseCaseTranslatedName(useCase)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Button
            onClick={handleSearch}
            disabled={!isValid}
            className="self-end"
          >
            {t('dashboard.runReport.startSearch.search')}
          </Button>
        </div>
      </div>
    </WeScreen>
  )
}
